import { detectFraming } from "./framing";
import { parseContainer } from "./versions";
import { mapCnhFields } from "./templates";
import { toHex } from "./bytes";

/**
 * The 64-character alphabet used by SERPRO's VIO system to encode CNH text
 * fields as a 6-bit-per-character bitstream. Covers uppercase ASCII, digits,
 * punctuation, and the `^` field separator.
 */
export const ALPHABET_6BIT =
  ' !"#$%&\'()*+,-./0123456789:;<=>?@ABCDEFGHIJKLMNOPQRSTUVWXYZ[\\]^_';

// ── Bitstream utilities ─────────────────────────────────────────────────

/**
 * Reads {@link numBits} bits from a byte array starting at {@link bitOffset}.
 * Bits are numbered MSB-first within each byte.
 */
export function readBits(data: Uint8Array, bitOffset: number, numBits: number): number {
  let value = 0;
  for (let i = 0; i < numBits; i++) {
    const byteIndex = Math.floor((bitOffset + i) / 8);
    const bitIndex = 7 - ((bitOffset + i) % 8);
    if (byteIndex < data.length) {
      value = (value << 1) | ((data[byteIndex]! >> bitIndex) & 1);
    }
  }
  return value;
}

/**
 * Decodes a byte array using SERPRO's 6-bit text encoding.
 * Each 6-bit group is mapped to a character in {@link ALPHABET_6BIT}.
 * Fields within the decoded string are separated by `^`.
 */
export function decode6bit(data: Uint8Array): string {
  let result = "";
  let bitPos = 0;
  while (bitPos + 6 <= data.length * 8) {
    const val = readBits(data, bitPos, 6);
    if (val >= 0 && val < ALPHABET_6BIT.length) {
      result += ALPHABET_6BIT[val];
    }
    bitPos += 6;
  }
  return result;
}

/** Reads a big-endian unsigned 16-bit integer from {@link data} at {@link offset}. */
export function uint16BE(data: Uint8Array, offset: number): number {
  return (data[offset]! << 8) | data[offset + 1]!;
}

// ── Types ───────────────────────────────────────────────────────────────

/**
 * Raw binary fields extracted from the CNH-e QR code.
 * These are the exact byte slices needed to reconstruct the signed payload
 * and verify the ECDSA signature.
 */
export interface CNHRawFields {
  /** Template identifier (uint32 from QR bytes 0–3). */
  templateId: number;
  /** QR format version (selects the payload parser). */
  version: number;
  /** Document signature: DER ECDSA (SHA256withECDSA, brainpoolP256r1) or a 256-byte RSA signature. */
  signature: Uint8Array;
  /** Field 0 — 2-byte identifier included in the signed payload. */
  field0: Uint8Array;
  /** Field 3 — 2-byte image-length prefix included in the signed payload. */
  field3: Uint8Array;
  /** BPG-compressed face photograph. */
  imageData: Uint8Array;
  /** Field 5 — 2-byte text-length prefix included in the signed payload. */
  field5: Uint8Array;
  /** Encoded text payload containing all document fields. */
  textData: Uint8Array;
  /**
   * The exact byte sequence the signature was computed over, reconstructed by
   * the version parser. When present, signature verification uses this directly
   * instead of {@link buildSignedData} (which only reproduces the v5 layout).
   */
  signedData?: Uint8Array;
}

/**
 * Parsed and decoded CNH (Carteira Nacional de Habilitacao) data.
 *
 * @example
 * ```ts
 * import { parseCNHQR } from "reader-cnh";
 *
 * const cnh = parseCNHQR(qrBinaryBuffer);
 * console.log(cnh.nome, cnh.cpf, cnh.validade);
 * ```
 */
export interface CNHData {
  /** Template identifier from the QR header. */
  templateId: number;
  /** QR format version. */
  version: number;
  /** Which outer framing the QR used: `hex` (timestamp+version prefix) or `binary` (id+version). */
  framing: "hex" | "binary";
  /** Document creation time (unix seconds) when the hex framing was used, else `null`. */
  timestamp: number | null;
  /** Raw positional field list (decoded text split on the version separator). */
  fields: string[];
  /**
   * Whether this QR version's byte layout is confirmed against a real document.
   * `false` for versions 1, 3 and 4 (implemented from decompilation, no sample).
   */
  versionVerified: boolean;
  /** Hex-encoded ECDSA signature. */
  signatureHex: string;
  /** Size of the embedded BPG face image in bytes. */
  imageDataLength: number;
  /** Raw binary fields for signature verification. */
  raw: CNHRawFields;

  /** Holder's name (short form). */
  nome: string;
  /** Holder's full name. */
  nomeCompleto: string;
  /** Identity document (RG) number + issuing authority. */
  identidade: string;
  /** CPF number (may include formatting dots/dash). */
  cpf: string;
  /** Date of birth (DD/MM/YYYY). */
  dataNascimento: string;
  /** Parent 1 (father) full name. */
  filiacao1: string;
  /** Parent 2 (mother) full name. */
  filiacao2: string;
  /** Observations / restrictions. */
  observacoes: string;
  /** Reserved field. */
  campo8: string;
  /** License category (A, B, C, D, E, or combinations). */
  categoria: string;
  /** RENACH registration number (11 digits). */
  registro: string;
  /** License expiry date (DD/MM/YYYY). */
  validade: string;
  /** Date of first license (DD/MM/YYYY). */
  primeiraHabilitacao: string;
  /** Reserved field. */
  campo14: string;
  /** Issuing city. */
  local: string;
  /** Issuing state (UF — 2-letter code). */
  uf: string;
  /** Issue date of this document (DD/MM/YYYY). */
  dataEmissao: string;
  /** Issuing authority local code. */
  codigoLocal: string;
  /** RENACH number. */
  renach: string;
}

// ── CPF validation ──────────────────────────────────────────────────────

/**
 * Validates a Brazilian CPF number using the standard mod-11 check-digit
 * algorithm. Accepts both formatted (`123.456.789-09`) and raw (`12345678909`)
 * input. Rejects all-same-digit sequences.
 */
export function validateCPF(cpf: string): boolean {
  const digits = cpf.replace(/\D/g, "");
  if (digits.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(digits)) return false;

  const calc = (len: number) => {
    let sum = 0;
    for (let i = 0; i < len; i++) {
      sum += parseInt(digits[i]!, 10) * (len + 1 - i);
    }
    const rem = sum % 11;
    return rem < 2 ? 0 : 11 - rem;
  };

  return calc(9) === parseInt(digits[9]!, 10) && calc(10) === parseInt(digits[10]!, 10);
}

// ── Date utilities ──────────────────────────────────────────────────────

/**
 * Validates a date string in `DD/MM/YYYY` format.
 * Checks calendar correctness (leap years, month lengths, etc.).
 */
export function isValidDate(dateStr: string): boolean {
  const match = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return false;
  const [, dd, mm, yyyy] = match;
  const day = parseInt(dd!, 10);
  const month = parseInt(mm!, 10);
  const year = parseInt(yyyy!, 10);
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  if (year < 1900 || year > 2100) return false;
  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
}

/** Parses a `DD/MM/YYYY` string into a `Date`, or returns `null` on failure. */
export function parseDate(dateStr: string): Date | null {
  const match = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return null;
  return new Date(parseInt(match[3]!, 10), parseInt(match[2]!, 10) - 1, parseInt(match[1]!, 10));
}

// ── QR binary parser ────────────────────────────────────────────────────

/**
 * Parses the raw binary data from a CNH-e QR code into a structured
 * {@link CNHData} object.
 *
 * The binary format (version 5) is:
 * ```
 * [0..3]  templateId (uint32 BE)
 * [4]     version (must be 5)
 * [5..]   payload:
 *           field0 (2 B) | sigLen (2 B) | signature (sigLen B) |
 *           imgLen (2 B) | image (imgLen B) |
 *           txtLen (2 B) | text (txtLen B)
 * ```
 *
 * Text is decoded from SERPRO's 6-bit alphabet; fields are `^`-separated.
 *
 * @param qrBytes - The complete binary content of the QR code.
 * @throws {Error} If the buffer is too short or the version is unsupported.
 */
export function parseCNHQR(qrBytes: Uint8Array): CNHData {
  if (qrBytes.length < 10) throw new Error("QR data too short");

  const framing = detectFraming(qrBytes);
  const container = parseContainer(framing);

  // field0 selects the field schema; the exposed templateId is the outer id for
  // the binary framing (back-compat) and field0 for the hex framing.
  const named = mapCnhFields(container.templateId, container.fields);

  const raw: CNHRawFields = {
    templateId: framing.id ?? container.templateId,
    version: container.version,
    signature: container.signature,
    field0: container.field0,
    field3: container.imgLenPrefix,
    imageData: container.imageData,
    field5: container.txtLenPrefix,
    textData: container.textData,
    signedData: container.signedData,
  };

  return {
    templateId: framing.id ?? container.templateId,
    version: container.version,
    framing: framing.framing,
    timestamp: container.timestamp,
    fields: container.fields,
    versionVerified: container.verified,
    signatureHex: toHex(container.signature),
    imageDataLength: container.imageDataLength,
    raw,
    nome: named.nome,
    nomeCompleto: named.nomeCompleto,
    identidade: named.identidade,
    cpf: named.cpf,
    dataNascimento: named.dataNascimento,
    filiacao1: named.filiacao1,
    filiacao2: named.filiacao2,
    observacoes: named.observacoes,
    campo8: named.permissao,
    categoria: named.categoria,
    registro: named.registro,
    validade: named.validade,
    primeiraHabilitacao: named.primeiraHabilitacao,
    campo14: named.acc,
    local: named.local,
    uf: named.uf,
    dataEmissao: named.dataEmissao,
    codigoLocal: named.codigoLocal,
    renach: named.renach,
  };
}

// [vendored] signature verification, fetchSerproCerts, buildSignedData and
// readCNH were removed — the browser pre-check never verifies or extracts.
